/**
 * Pure validator for the IntakePage form submission.
 *
 * Extracted from IntakePage.jsx so it can be PBT-tested with hundreds
 * of synthetic templates + response combinations per second. This is
 * the function that decides "can the patient submit this page?" — a
 * bug here means the form silently refuses to submit (Chris Davis's
 * complaint 2026-06-05) OR accepts incomplete data (audit risk).
 *
 * Pure: no React state, no DOM refs, no DB. Pass everything in.
 *
 * @param {Object} args
 * @param {Object} args.template       — form template { id, fields:[],
 *                                       requires_signature?:bool }
 * @param {Object} args.responses      — { [fieldId]: value }
 * @param {boolean} args.hasSignature  — true if a signature has been
 *                                       captured (from either state or
 *                                       the live pad ref)
 * @returns {{
 *   ok: boolean,
 *   missingFieldIds: string[],
 *   missingLabels: string[],
 *   signatureMissing: boolean,
 * }}
 */
import { isFieldVisible } from './formConditions.js';

function validateIntakePage({ template, responses, hasSignature, patientGender }) {
  // Defensive guards — template may be undefined during initial render,
  // responses may be {} or undefined, hasSignature may be undefined.
  const safeTemplate = template && typeof template === 'object' ? template : {};
  const safeResponses = responses && typeof responses === 'object' ? responses : {};
  let fields = safeTemplate.fields;
  if (typeof fields === 'string') {
    try { fields = JSON.parse(fields); } catch { fields = []; }
  }
  if (!Array.isArray(fields)) fields = [];

  // Whitelist of renderable input field types. Anything else (header,
  // divider, instructions, subheader, etc.) cannot be validated as
  // required because there's no UI path to fill it. Admin-marked
  // required non-input fields were silently blocking submission
  // forever.
  const RENDERABLE_INPUTS = new Set([
    'text', 'email', 'phone', 'date', 'textarea', 'select', 'radio',
    'checkbox', 'checkbox_group', 'pain_scale', 'address', 'photo',
  ]);

  const required = fields.filter((f) =>
    f && typeof f === 'object'
    && f.required === true
    && typeof f.type === 'string'
    && RENDERABLE_INPUTS.has(f.type)
    // A required field inside a hidden conditional branch (show_if not
    // satisfied, or a gendered section for the other sex) is not on
    // screen — it must not block submission.
    && isFieldVisible(f, safeResponses, patientGender),
  );

  // Loose semantics — any defined non-null non-empty-string value
  // counts as filled. Partially-filled objects (e.g. address with city
  // but no street) still pass. Empty arrays and empty objects also
  // pass — the patient touched the field. We rely on per-field UI
  // (placeholder, asterisks, type-specific guidance) to communicate
  // what "filled enough" means for each field type.
  const missing = required.filter((f) => {
    // hasOwn guards against inherited keys like '__proto__' / 'toString'
    // (an admin or migration glitch can land any string as a field id).
    // Without this, responses['__proto__'] resolves to Object.prototype
    // and the field reads as "filled" forever.
    if (!Object.prototype.hasOwnProperty.call(safeResponses, f.id)) return true;
    const val = safeResponses[f.id];
    return val === undefined || val === null || val === '';
  });

  const missingFieldIds = missing.map((f) => f.id);
  const missingLabels = missing.map((f) => f.label || f.id);

  let signatureMissing = false;
  if (safeTemplate.requires_signature === true) {
    if (!hasSignature) {
      signatureMissing = true;
      missingLabels.push('Signature');
    }
  }

  return {
    ok: missingFieldIds.length === 0 && !signatureMissing,
    missingFieldIds,
    missingLabels,
    signatureMissing,
  };
}

export { validateIntakePage };
